import { useEffect, useMemo, useState, type FormEvent } from "react";
import * as QRCode from "qrcode";
import { apiFetch, ApiError } from "@/lib/api-client";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/i18n";

type FinanceTab = "packages" | "memberships" | "gift-cards" | "promo-codes" | "ledger";

interface CreditPackage {
  id: string;
  name: string;
  description: string | null;
  accountType: string;
  creditAmount: number;
  priceCents: number;
  currency: string;
  expiresInDays: number | null;
  isActive: boolean;
  sortOrder: number;
}

interface GiftCard {
  id: string;
  code: string;
  initialBalanceCents: number;
  currentBalanceCents: number;
  currency: string;
  status: string;
  expiresAt: string | null;
}

interface PromoCode {
  id: string;
  code: string;
  promoType: "percent_off" | "fixed_off" | "free_service";
  value: number | null;
  usageLimit: number | null;
  usageCount: number;
  minimumSpendCents: number | null;
  validTo: string | null;
  isActive: boolean;
}

interface MembershipPlan {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  priceCents: number;
  currency: string;
  billingInterval: "week" | "month" | "quarter" | "year";
  status: "draft" | "active" | "archived";
  includedCredits: Array<{ accountType: string; amount: number }>;
}

interface ServiceMembership {
  id: string;
  personId: string;
  planId: string;
  status: "trialing" | "active" | "past_due" | "canceled" | "expired";
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

interface CreditAccount {
  id: string;
  personId: string;
  personName: string;
  personEmail: string;
  accountType: string;
  balance: number;
  totalPurchased: number;
  totalUsed: number;
  expiresAt: string | null;
  packageName: string | null;
}

interface CreditTransaction {
  id: string;
  type: "purchase" | "usage" | "refund" | "manual_add" | "gift" | "expiry";
  amount: number;
  description: string;
  createdAt: string;
}

interface CreditPackageForm {
  name: string;
  description: string;
  accountType: string;
  creditAmount: string;
  priceCents: string;
  currency: string;
  expiresInDays: string;
  sortOrder: string;
  isActive: boolean;
}

interface GiftCardForm {
  initialBalanceCents: string;
  currency: string;
  purchasedBy: string;
  expiresAt: string;
  note: string;
}

interface PromoCodeForm {
  code: string;
  promoType: "percent_off" | "fixed_off" | "free_service";
  value: string;
  usageLimit: string;
  minimumSpendCents: string;
  validTo: string;
  isActive: boolean;
}

interface MembershipPlanForm {
  slug: string;
  name: string;
  description: string;
  priceCents: string;
  currency: string;
  billingInterval: "week" | "month" | "quarter" | "year";
  status: "draft" | "active" | "archived";
  includedCreditsAccountType: string;
  includedCreditsAmount: string;
}

interface MembershipForm {
  personId: string;
  planId: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

interface ToastState {
  tone: "success" | "error";
  message: string;
}

const DEFAULT_PACKAGE_FORM: CreditPackageForm = {
  name: "",
  description: "",
  accountType: "session_credits",
  creditAmount: "10",
  priceCents: "0",
  currency: "EUR",
  expiresInDays: "",
  sortOrder: "0",
  isActive: true,
};

const DEFAULT_GIFT_CARD_FORM: GiftCardForm = {
  initialBalanceCents: "5000",
  currency: "EUR",
  purchasedBy: "",
  expiresAt: "",
  note: "",
};

const DEFAULT_PROMO_FORM: PromoCodeForm = {
  code: "",
  promoType: "percent_off",
  value: "10",
  usageLimit: "",
  minimumSpendCents: "",
  validTo: "",
  isActive: true,
};

const DEFAULT_MEMBERSHIP_PLAN_FORM: MembershipPlanForm = {
  slug: "",
  name: "",
  description: "",
  priceCents: "0",
  currency: "EUR",
  billingInterval: "month",
  status: "active",
  includedCreditsAccountType: "session_credits",
  includedCreditsAmount: "0",
};

const DEFAULT_MEMBERSHIP_FORM: MembershipForm = {
  personId: "",
  planId: "",
  currentPeriodStart: "",
  currentPeriodEnd: "",
  cancelAtPeriodEnd: false,
};

function createReferenceId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "00000000-0000-4000-8000-000000000000";
}

function formatExpiry(iso: string | null) {
  if (!iso) return "No expiry";
  return formatDate(iso);
}

function toLocalDateTimeValue(iso: string | null) {
  if (!iso) return "";
  const date = new Date(iso);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60_000);
  return local.toISOString().slice(0, 16);
}

function toIsoDateTime(value: string) {
  if (!value) return undefined;
  return new Date(value).toISOString();
}

function buildPackageForm(pkg: CreditPackage): CreditPackageForm {
  return {
    name: pkg.name,
    description: pkg.description ?? "",
    accountType: pkg.accountType,
    creditAmount: String(pkg.creditAmount),
    priceCents: String(pkg.priceCents),
    currency: pkg.currency,
    expiresInDays: pkg.expiresInDays === null ? "" : String(pkg.expiresInDays),
    sortOrder: String(pkg.sortOrder),
    isActive: pkg.isActive,
  };
}

export default function CreditsPage() {
  const [activeTab, setActiveTab] = useState<FinanceTab>("packages");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  const [creditPackages, setCreditPackages] = useState<CreditPackage[]>([]);
  const [membershipPlans, setMembershipPlans] = useState<MembershipPlan[]>([]);
  const [memberships, setMemberships] = useState<ServiceMembership[]>([]);
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [accounts, setAccounts] = useState<CreditAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<CreditAccount | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);

  const [editingPackageId, setEditingPackageId] = useState<string | null>(null);
  const [creditPackageForm, setCreditPackageForm] = useState<CreditPackageForm>(DEFAULT_PACKAGE_FORM);
  const [membershipPlanForm, setMembershipPlanForm] = useState<MembershipPlanForm>(DEFAULT_MEMBERSHIP_PLAN_FORM);
  const [membershipForm, setMembershipForm] = useState<MembershipForm>(DEFAULT_MEMBERSHIP_FORM);
  const [giftCardForm, setGiftCardForm] = useState<GiftCardForm>(DEFAULT_GIFT_CARD_FORM);
  const [promoCodeForm, setPromoCodeForm] = useState<PromoCodeForm>(DEFAULT_PROMO_FORM);
  const [manualAddAmount, setManualAddAmount] = useState("1");
  const [refundDraft, setRefundDraft] = useState<{ transactionId: string; maxAmount: number; amount: string } | null>(null);
  const [selectedGiftCardId, setSelectedGiftCardId] = useState<string | null>(null);
  const [giftCardQr, setGiftCardQr] = useState<string | null>(null);

  const selectedGiftCard = useMemo(
    () => giftCards.find((giftCard) => giftCard.id === selectedGiftCardId) ?? null,
    [giftCards, selectedGiftCardId],
  );

  async function loadFinanceData() {
    const [packagesResult, membershipPlansResult, membershipsResult, giftCardsResult, promoCodesResult, accountsResult] = await Promise.all([
      apiFetch<{ creditPackages: CreditPackage[] }>("/crm/bookings/credit-packages"),
      apiFetch<{ data: MembershipPlan[] }>("/crm/bookings/membership-plans"),
      apiFetch<{ data: ServiceMembership[] }>("/crm/bookings/memberships"),
      apiFetch<{ giftCards: GiftCard[] }>("/crm/bookings/gift-cards"),
      apiFetch<{ promoCodes: PromoCode[] }>("/crm/bookings/promo-codes"),
      apiFetch<{ accounts: CreditAccount[] }>("/crm/bookings/credits"),
    ]);

    setCreditPackages(packagesResult.creditPackages);
    setMembershipPlans(membershipPlansResult.data);
    setMemberships(membershipsResult.data);
    setGiftCards(giftCardsResult.giftCards);
    setPromoCodes(promoCodesResult.promoCodes);
    setAccounts(accountsResult.accounts);

    const nextSelectedAccount =
      accountsResult.accounts.find((account) => account.id === selectedAccount?.id)
      ?? accountsResult.accounts[0]
      ?? null;
    setSelectedAccount(nextSelectedAccount);
  }

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await loadFinanceData();
      } catch (error) {
        if (!cancelled) {
          setToast({ tone: "error", message: error instanceof Error ? error.message : "Failed to load finance data" });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedAccount) {
      setTransactions([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const result = await apiFetch<{ transactions: CreditTransaction[] }>(
          `/crm/bookings/credits/${selectedAccount.id}/transactions`,
        );
        if (!cancelled) {
          setTransactions(result.transactions);
        }
      } catch (error) {
        if (!cancelled) {
          setTransactions([]);
          setToast({ tone: "error", message: error instanceof Error ? error.message : "Failed to load ledger" });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedGiftCard) {
      setGiftCardQr(null);
      return;
    }

    let cancelled = false;

    QRCode.toDataURL(`gift-card:${selectedGiftCard.code}`, {
      margin: 1,
      width: 220,
      color: {
        dark: "#101828",
        light: "#ffffff",
      },
    }).then((dataUrl) => {
      if (!cancelled) {
        setGiftCardQr(dataUrl);
      }
    }).catch(() => {
      if (!cancelled) {
        setGiftCardQr(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [selectedGiftCard]);

  async function refreshAccounts() {
    const result = await apiFetch<{ accounts: CreditAccount[] }>("/crm/bookings/credits");
    setAccounts(result.accounts);
    const nextSelectedAccount =
      result.accounts.find((account) => account.id === selectedAccount?.id)
      ?? result.accounts[0]
      ?? null;
    setSelectedAccount(nextSelectedAccount);
  }

  async function handlePackageSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = {
        name: creditPackageForm.name,
        description: creditPackageForm.description.trim() || null,
        accountType: creditPackageForm.accountType,
        creditAmount: Number(creditPackageForm.creditAmount),
        priceCents: Number(creditPackageForm.priceCents),
        currency: creditPackageForm.currency.toUpperCase(),
        expiresInDays: creditPackageForm.expiresInDays === "" ? null : Number(creditPackageForm.expiresInDays),
        sortOrder: Number(creditPackageForm.sortOrder),
        isActive: creditPackageForm.isActive,
      };

      if (editingPackageId) {
        await apiFetch(`/crm/bookings/credit-packages/${editingPackageId}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch("/crm/bookings/credit-packages", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }

      await loadFinanceData();
      setEditingPackageId(null);
      setCreditPackageForm(DEFAULT_PACKAGE_FORM);
      setToast({ tone: "success", message: editingPackageId ? "Credit package updated" : "Credit package created" });
    } catch (error) {
      setToast({ tone: "error", message: error instanceof Error ? error.message : "Unable to save credit package" });
    } finally {
      setSaving(false);
    }
  }

  async function handleArchivePackage(packageId: string) {
    setSaving(true);
    try {
      await apiFetch(`/crm/bookings/credit-packages/${packageId}`, { method: "DELETE" });
      await loadFinanceData();
      setToast({ tone: "success", message: "Credit package archived" });
    } catch (error) {
      setToast({ tone: "error", message: error instanceof Error ? error.message : "Unable to archive package" });
    } finally {
      setSaving(false);
    }
  }

  async function handleGiftCardSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);

    try {
      await apiFetch("/crm/bookings/gift-cards", {
        method: "POST",
        body: JSON.stringify({
          initialBalanceCents: Number(giftCardForm.initialBalanceCents),
          currency: giftCardForm.currency.toUpperCase(),
          purchasedBy: giftCardForm.purchasedBy,
          expiresAt: toIsoDateTime(giftCardForm.expiresAt),
          note: giftCardForm.note.trim() || undefined,
        }),
      });
      await loadFinanceData();
      setGiftCardForm(DEFAULT_GIFT_CARD_FORM);
      setToast({ tone: "success", message: "Gift card issued" });
    } catch (error) {
      setToast({ tone: "error", message: error instanceof Error ? error.message : "Unable to issue gift card" });
    } finally {
      setSaving(false);
    }
  }

  async function handleMembershipPlanSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);

    const includedCreditsAmount = Number(membershipPlanForm.includedCreditsAmount);

    try {
      await apiFetch("/crm/bookings/membership-plans", {
        method: "POST",
        body: JSON.stringify({
          slug: membershipPlanForm.slug,
          name: membershipPlanForm.name,
          description: membershipPlanForm.description.trim() || null,
          priceCents: Number(membershipPlanForm.priceCents),
          currency: membershipPlanForm.currency.toUpperCase(),
          billingInterval: membershipPlanForm.billingInterval,
          status: membershipPlanForm.status,
          includedCredits: includedCreditsAmount > 0
            ? [{
              accountType: membershipPlanForm.includedCreditsAccountType,
              amount: includedCreditsAmount,
            }]
            : [],
          renewalRules: {
            renewOnPaymentSuccess: true,
          },
        }),
      });
      await loadFinanceData();
      setMembershipPlanForm(DEFAULT_MEMBERSHIP_PLAN_FORM);
      setToast({ tone: "success", message: "Membership plan created" });
    } catch (error) {
      setToast({ tone: "error", message: error instanceof Error ? error.message : "Unable to create membership plan" });
    } finally {
      setSaving(false);
    }
  }

  async function handleMembershipSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);

    try {
      await apiFetch("/crm/bookings/memberships", {
        method: "POST",
        body: JSON.stringify({
          personId: membershipForm.personId,
          planId: membershipForm.planId,
          currentPeriodStart: membershipForm.currentPeriodStart
            ? toIsoDateTime(membershipForm.currentPeriodStart)
            : new Date().toISOString(),
          currentPeriodEnd: membershipForm.currentPeriodEnd
            ? toIsoDateTime(membershipForm.currentPeriodEnd)
            : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          cancelAtPeriodEnd: membershipForm.cancelAtPeriodEnd,
        }),
      });
      await loadFinanceData();
      setMembershipForm(DEFAULT_MEMBERSHIP_FORM);
      setToast({ tone: "success", message: "Membership activated" });
    } catch (error) {
      setToast({ tone: "error", message: error instanceof Error ? error.message : "Unable to activate membership" });
    } finally {
      setSaving(false);
    }
  }

  async function cancelMembership(membershipId: string) {
    setSaving(true);

    try {
      await apiFetch(`/crm/bookings/memberships/${membershipId}/cancel`, {
        method: "POST",
        body: JSON.stringify({
          effectiveAt: new Date().toISOString(),
          cancelAtPeriodEnd: true,
        }),
      });
      await loadFinanceData();
      setToast({ tone: "success", message: "Membership cancellation scheduled" });
    } catch (error) {
      setToast({ tone: "error", message: error instanceof Error ? error.message : "Unable to cancel membership" });
    } finally {
      setSaving(false);
    }
  }

  async function handlePromoCodeSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);

    try {
      await apiFetch("/crm/bookings/promo-codes", {
        method: "POST",
        body: JSON.stringify({
          code: promoCodeForm.code,
          promoType: promoCodeForm.promoType,
          value: promoCodeForm.promoType === "free_service" ? undefined : Number(promoCodeForm.value),
          usageLimit: promoCodeForm.usageLimit === "" ? null : Number(promoCodeForm.usageLimit),
          perCustomerLimit: 0,
          minimumSpendCents: promoCodeForm.minimumSpendCents === "" ? null : Number(promoCodeForm.minimumSpendCents),
          validTo: promoCodeForm.validTo ? toIsoDateTime(promoCodeForm.validTo) : null,
          isActive: promoCodeForm.isActive,
        }),
      });
      await loadFinanceData();
      setPromoCodeForm(DEFAULT_PROMO_FORM);
      setToast({ tone: "success", message: "Promo code created" });
    } catch (error) {
      setToast({ tone: "error", message: error instanceof Error ? error.message : "Unable to create promo code" });
    } finally {
      setSaving(false);
    }
  }

  async function togglePromoCode(promoCode: PromoCode) {
    setSaving(true);
    try {
      await apiFetch(`/crm/bookings/promo-codes/${promoCode.id}`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: !promoCode.isActive }),
      });
      await loadFinanceData();
      setToast({ tone: "success", message: promoCode.isActive ? "Promo code paused" : "Promo code reactivated" });
    } catch (error) {
      setToast({ tone: "error", message: error instanceof Error ? error.message : "Unable to update promo code" });
    } finally {
      setSaving(false);
    }
  }

  async function addManualCredits(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedAccount) return;
    setSaving(true);

    try {
      await apiFetch("/crm/bookings/credits/purchase", {
        method: "POST",
        body: JSON.stringify({
          personId: selectedAccount.personId,
          accountType: selectedAccount.accountType,
          amount: Number(manualAddAmount),
          currency: "EUR",
          referenceType: "manual_adjustment",
          referenceId: createReferenceId(),
        }),
      });
      await refreshAccounts();
      const ledger = await apiFetch<{ transactions: CreditTransaction[] }>(`/crm/bookings/credits/${selectedAccount.id}/transactions`);
      setTransactions(ledger.transactions);
      setManualAddAmount("1");
      setToast({ tone: "success", message: "Credits added" });
    } catch (error) {
      setToast({ tone: "error", message: error instanceof Error ? error.message : "Unable to add credits" });
    } finally {
      setSaving(false);
    }
  }

  async function submitRefund(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!refundDraft) return;
    setSaving(true);

    try {
      await apiFetch("/crm/bookings/refunds", {
        method: "POST",
        body: JSON.stringify({
          transactionId: refundDraft.transactionId,
          amount: Number(refundDraft.amount),
        }),
      });
      if (selectedAccount) {
        await refreshAccounts();
        const ledger = await apiFetch<{ transactions: CreditTransaction[] }>(`/crm/bookings/credits/${selectedAccount.id}/transactions`);
        setTransactions(ledger.transactions);
      }
      setRefundDraft(null);
      setToast({ tone: "success", message: "Refund processed" });
    } catch (error) {
      const message = error instanceof ApiError && error.status === 403
        ? "Manager role required for refunds"
        : error instanceof Error
          ? error.message
          : "Unable to process refund";
      setToast({ tone: "error", message });
    } finally {
      setSaving(false);
    }
  }

  function renderTabButton(tab: FinanceTab, label: string) {
    const isActive = activeTab === tab;
    return (
      <button
        type="button"
        onClick={() => setActiveTab(tab)}
        className={`rounded-full px-4 py-2 text-sm font-medium transition ${isActive ? "bg-[var(--color-primary)] text-white" : "border border-[var(--color-border)] text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"}`}
      >
        {label}
      </button>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Service Finance</h1>
          <p className="text-sm text-muted-foreground">
            Credit packages, memberships, gift cards, promo previews, and the customer ledger on one mounted runtime.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Finance sections">
          {renderTabButton("packages", "Packages")}
          {renderTabButton("memberships", "Memberships")}
          {renderTabButton("gift-cards", "Gift Cards")}
          {renderTabButton("promo-codes", "Promo Codes")}
          {renderTabButton("ledger", "Ledger")}
        </div>
      </div>

      {toast ? (
        <div className={`rounded-xl border px-4 py-3 text-sm ${toast.tone === "success" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-900" : "border-rose-500/30 bg-rose-500/10 text-rose-900"}`}>
          {toast.message}
        </div>
      ) : null}

      {activeTab === "packages" ? (
        <div className="grid gap-6 xl:grid-cols-[1.1fr,0.9fr]">
          <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Credit packages</h2>
                <p className="text-sm text-[var(--color-muted-foreground)]">Define reusable credit or hour packs; checkout purchase flow is not mounted here yet.</p>
              </div>
              <div className="text-sm text-[var(--color-muted-foreground)]">{creditPackages.length} package definitions</div>
            </div>
            <div className="mt-4 space-y-3">
              {creditPackages.map((pkg) => (
                <div key={pkg.id} className="rounded-xl border border-[var(--color-border)] p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-[var(--color-foreground)]">{pkg.name}</h3>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${pkg.isActive ? "bg-emerald-500/10 text-emerald-700" : "bg-slate-500/10 text-slate-600"}`}>
                          {pkg.isActive ? "active" : "archived"}
                        </span>
                      </div>
                      <p className="text-sm text-[var(--color-muted-foreground)]">{pkg.description || "No description"}</p>
                      <div className="flex flex-wrap gap-3 text-xs text-[var(--color-muted-foreground)]">
                        <span>{pkg.creditAmount} credits</span>
                        <span>{formatCurrency(pkg.priceCents, pkg.currency)}</span>
                        <span>{pkg.accountType}</span>
                        <span>{pkg.expiresInDays === null ? "No expiry" : `Expires in ${pkg.expiresInDays} days`}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingPackageId(pkg.id);
                          setCreditPackageForm(buildPackageForm(pkg));
                        }}
                        className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                      >
                        Edit
                      </button>
                      {pkg.isActive ? (
                        <button
                          type="button"
                          onClick={() => void handleArchivePackage(pkg.id)}
                          className="rounded-lg border border-rose-500/30 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-500/10"
                        >
                          Archive
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <form onSubmit={handlePackageSubmit} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">{editingPackageId ? "Edit package" : "Create package"}</h2>
            <div className="mt-4 grid gap-3">
              <input value={creditPackageForm.name} onChange={(event) => setCreditPackageForm((current) => ({ ...current, name: event.target.value }))} placeholder="Package name" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
              <textarea value={creditPackageForm.description} onChange={(event) => setCreditPackageForm((current) => ({ ...current, description: event.target.value }))} placeholder="Description" className="min-h-24 rounded-xl border border-[var(--color-border)] px-3 py-2" />
              <div className="grid gap-3 md:grid-cols-2">
                <input value={creditPackageForm.accountType} onChange={(event) => setCreditPackageForm((current) => ({ ...current, accountType: event.target.value }))} placeholder="Account type" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                <input value={creditPackageForm.creditAmount} onChange={(event) => setCreditPackageForm((current) => ({ ...current, creditAmount: event.target.value }))} placeholder="Credit amount" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                <input value={creditPackageForm.priceCents} onChange={(event) => setCreditPackageForm((current) => ({ ...current, priceCents: event.target.value }))} placeholder="Price cents" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                <input value={creditPackageForm.currency} onChange={(event) => setCreditPackageForm((current) => ({ ...current, currency: event.target.value }))} placeholder="Currency" className="rounded-xl border border-[var(--color-border)] px-3 py-2 uppercase" />
                <input value={creditPackageForm.expiresInDays} onChange={(event) => setCreditPackageForm((current) => ({ ...current, expiresInDays: event.target.value }))} placeholder="Expires in days" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                <input value={creditPackageForm.sortOrder} onChange={(event) => setCreditPackageForm((current) => ({ ...current, sortOrder: event.target.value }))} placeholder="Sort order" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
              </div>
              <label className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
                <input type="checkbox" checked={creditPackageForm.isActive} onChange={(event) => setCreditPackageForm((current) => ({ ...current, isActive: event.target.checked }))} />
                Active
              </label>
            </div>
            <div className="mt-4 flex gap-3">
              <button type="submit" disabled={saving} className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
                {editingPackageId ? "Save package" : "Create package"}
              </button>
              {editingPackageId ? (
                <button type="button" onClick={() => {
                  setEditingPackageId(null);
                  setCreditPackageForm(DEFAULT_PACKAGE_FORM);
                }} className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)]">
                  Reset
                </button>
              ) : null}
            </div>
          </form>
        </div>
      ) : null}

      {activeTab === "memberships" ? (
        <div className="grid gap-6 xl:grid-cols-[1.1fr,0.9fr]">
          <section className="space-y-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <div>
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Membership plans</h2>
              <p className="text-sm text-[var(--color-muted-foreground)]">Recurring plan and subscription records; billing automation and included-credit grants are not auto-applied yet.</p>
            </div>
            <div className="space-y-3">
              {membershipPlans.map((plan) => (
                <div key={plan.id} className="rounded-xl border border-[var(--color-border)] p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-[var(--color-foreground)]">{plan.name}</h3>
                        <span className="rounded-full bg-slate-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">{plan.status}</span>
                      </div>
                      <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{plan.description || "No description"}</p>
                      <div className="mt-2 flex flex-wrap gap-3 text-xs text-[var(--color-muted-foreground)]">
                        <span>{formatCurrency(plan.priceCents, plan.currency)} / {plan.billingInterval}</span>
                        <span>{plan.slug}</span>
                        <span>
                          {plan.includedCredits.length
                            ? plan.includedCredits.map((credit) => `${credit.amount} ${credit.accountType}`).join(", ")
                            : "No included credits"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {membershipPlans.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[var(--color-border)] p-4 text-sm text-[var(--color-muted-foreground)]">
                  No membership plans yet.
                </div>
              ) : null}
            </div>

            <div className="pt-2">
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Active memberships</h2>
              <div className="mt-3 space-y-3">
                {memberships.map((membership) => {
                  const plan = membershipPlans.find((candidate) => candidate.id === membership.planId);
                  return (
                    <div key={membership.id} className="rounded-xl border border-[var(--color-border)] p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-[var(--color-foreground)]">{plan?.name ?? "Unknown plan"}</h3>
                            <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">{membership.status}</span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-3 text-xs text-[var(--color-muted-foreground)]">
                            <span>Person {membership.personId}</span>
                            <span>{formatDate(membership.currentPeriodStart)} → {formatDate(membership.currentPeriodEnd)}</span>
                            <span>{membership.cancelAtPeriodEnd ? "Cancels at period end" : "Renews"}</span>
                          </div>
                        </div>
                        {membership.status !== "canceled" ? (
                          <button
                            type="button"
                            onClick={() => void cancelMembership(membership.id)}
                            className="rounded-lg border border-rose-500/30 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-500/10"
                          >
                            Cancel at period end
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
                {memberships.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-[var(--color-border)] p-4 text-sm text-[var(--color-muted-foreground)]">
                    No active memberships yet.
                  </div>
                ) : null}
              </div>
            </div>
          </section>

          <div className="space-y-6">
            <form onSubmit={handleMembershipPlanSubmit} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Create membership plan</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">This defines the plan record. Stripe billing and automatic credit grants remain separate automation.</p>
              <div className="mt-4 grid gap-3">
                <input value={membershipPlanForm.name} onChange={(event) => setMembershipPlanForm((current) => ({ ...current, name: event.target.value }))} placeholder="Plan name" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                <input value={membershipPlanForm.slug} onChange={(event) => setMembershipPlanForm((current) => ({ ...current, slug: event.target.value }))} placeholder="Plan slug" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                <textarea value={membershipPlanForm.description} onChange={(event) => setMembershipPlanForm((current) => ({ ...current, description: event.target.value }))} placeholder="Plan description" className="min-h-20 rounded-xl border border-[var(--color-border)] px-3 py-2" />
                <div className="grid gap-3 md:grid-cols-2">
                  <input value={membershipPlanForm.priceCents} onChange={(event) => setMembershipPlanForm((current) => ({ ...current, priceCents: event.target.value }))} placeholder="Plan price cents" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                  <input value={membershipPlanForm.currency} onChange={(event) => setMembershipPlanForm((current) => ({ ...current, currency: event.target.value }))} placeholder="Currency" className="rounded-xl border border-[var(--color-border)] px-3 py-2 uppercase" />
                  <select value={membershipPlanForm.billingInterval} onChange={(event) => setMembershipPlanForm((current) => ({ ...current, billingInterval: event.target.value as MembershipPlanForm["billingInterval"] }))} className="rounded-xl border border-[var(--color-border)] px-3 py-2">
                    <option value="week">Weekly</option>
                    <option value="month">Monthly</option>
                    <option value="quarter">Quarterly</option>
                    <option value="year">Yearly</option>
                  </select>
                  <select value={membershipPlanForm.status} onChange={(event) => setMembershipPlanForm((current) => ({ ...current, status: event.target.value as MembershipPlanForm["status"] }))} className="rounded-xl border border-[var(--color-border)] px-3 py-2">
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                  <input value={membershipPlanForm.includedCreditsAccountType} onChange={(event) => setMembershipPlanForm((current) => ({ ...current, includedCreditsAccountType: event.target.value }))} placeholder="Included credit type" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                  <input value={membershipPlanForm.includedCreditsAmount} onChange={(event) => setMembershipPlanForm((current) => ({ ...current, includedCreditsAmount: event.target.value }))} placeholder="Included credits amount" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                </div>
              </div>
              <button type="submit" disabled={saving} className="mt-4 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
                Create membership plan
              </button>
            </form>

            <form onSubmit={handleMembershipSubmit} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Activate membership</h2>
              <div className="mt-4 grid gap-3">
                <input value={membershipForm.personId} onChange={(event) => setMembershipForm((current) => ({ ...current, personId: event.target.value }))} placeholder="Membership person ID" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                <select value={membershipForm.planId} onChange={(event) => setMembershipForm((current) => ({ ...current, planId: event.target.value }))} className="rounded-xl border border-[var(--color-border)] px-3 py-2">
                  <option value="">Select membership plan</option>
                  {membershipPlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>{plan.name}</option>
                  ))}
                </select>
                <div className="grid gap-3 md:grid-cols-2">
                  <input type="datetime-local" value={membershipForm.currentPeriodStart} onChange={(event) => setMembershipForm((current) => ({ ...current, currentPeriodStart: event.target.value }))} className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                  <input type="datetime-local" value={membershipForm.currentPeriodEnd} onChange={(event) => setMembershipForm((current) => ({ ...current, currentPeriodEnd: event.target.value }))} className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                </div>
                <label className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
                  <input type="checkbox" checked={membershipForm.cancelAtPeriodEnd} onChange={(event) => setMembershipForm((current) => ({ ...current, cancelAtPeriodEnd: event.target.checked }))} />
                  Cancel at period end
                </label>
              </div>
              <button type="submit" disabled={saving || membershipPlans.length === 0} className="mt-4 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
                Activate membership
              </button>
            </form>
          </div>
        </div>
      ) : null}

      {activeTab === "gift-cards" ? (
        <div className="grid gap-6 xl:grid-cols-[0.95fr,1.05fr]">
          <form onSubmit={handleGiftCardSubmit} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Issue gift card</h2>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Create a stored-value card and print a branded QR code card. Delivery and redemption are staff-operated unless checkout calls the redeem endpoint.</p>
            <div className="mt-4 grid gap-3">
              <input value={giftCardForm.purchasedBy} onChange={(event) => setGiftCardForm((current) => ({ ...current, purchasedBy: event.target.value }))} placeholder="Purchased by person ID" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
              <div className="grid gap-3 md:grid-cols-2">
                <input value={giftCardForm.initialBalanceCents} onChange={(event) => setGiftCardForm((current) => ({ ...current, initialBalanceCents: event.target.value }))} placeholder="Initial balance (cents)" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                <input value={giftCardForm.currency} onChange={(event) => setGiftCardForm((current) => ({ ...current, currency: event.target.value }))} placeholder="Currency" className="rounded-xl border border-[var(--color-border)] px-3 py-2 uppercase" />
              </div>
              <input type="datetime-local" value={giftCardForm.expiresAt} onChange={(event) => setGiftCardForm((current) => ({ ...current, expiresAt: event.target.value }))} className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
              <textarea value={giftCardForm.note} onChange={(event) => setGiftCardForm((current) => ({ ...current, note: event.target.value }))} placeholder="Internal note" className="min-h-24 rounded-xl border border-[var(--color-border)] px-3 py-2" />
            </div>
            <button type="submit" disabled={saving} className="mt-4 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
              Issue gift card
            </button>
          </form>

          <section className="grid gap-6 lg:grid-cols-[0.9fr,1.1fr]">
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Issued cards</h2>
              <div className="mt-4 space-y-3">
                {giftCards.map((giftCard) => (
                  <button
                    key={giftCard.id}
                    type="button"
                    onClick={() => setSelectedGiftCardId(giftCard.id)}
                    className={`w-full rounded-xl border p-4 text-left ${selectedGiftCardId === giftCard.id ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5" : "border-[var(--color-border)] hover:border-[var(--color-primary)]/40"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-[var(--color-foreground)]">{giftCard.code}</span>
                      <span className="text-sm text-[var(--color-muted-foreground)]">{formatCurrency(giftCard.currentBalanceCents, giftCard.currency)}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-[var(--color-muted-foreground)]">
                      <span>Status: {giftCard.status}</span>
                      <span>Issued: {formatCurrency(giftCard.initialBalanceCents, giftCard.currency)}</span>
                      <span>{formatExpiry(giftCard.expiresAt)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              {selectedGiftCard ? (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-dashed border-[var(--color-primary)]/40 bg-gradient-to-br from-[var(--color-primary)]/10 via-white to-[var(--color-surface)] p-6 print:border-none print:bg-white">
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-muted-foreground)]">Gift Card</p>
                    <h2 className="mt-2 text-3xl font-semibold text-[var(--color-foreground)]">{selectedGiftCard.code}</h2>
                    <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                      Stored value: {formatCurrency(selectedGiftCard.initialBalanceCents, selectedGiftCard.currency)}
                    </p>
                    <div className="mt-6 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                      <div className="space-y-1 text-sm text-[var(--color-muted-foreground)]">
                        <p>Remaining balance: {formatCurrency(selectedGiftCard.currentBalanceCents, selectedGiftCard.currency)}</p>
                        <p>Expires: {formatExpiry(selectedGiftCard.expiresAt)}</p>
                        <p>Scan or enter this code in a staff redemption or checkout redeem flow.</p>
                      </div>
                      {giftCardQr ? (
                        <img src={giftCardQr} alt={`QR code for ${selectedGiftCard.code}`} className="h-44 w-44 rounded-xl bg-white p-3" />
                      ) : (
                        <div className="flex h-44 w-44 items-center justify-center rounded-xl border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)]">
                          Generating QR...
                        </div>
                      )}
                    </div>
                  </div>
                  <button type="button" onClick={() => window.print()} className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]">
                    Print gift card
                  </button>
                </div>
              ) : (
                <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-[var(--color-border)] p-8 text-sm text-[var(--color-muted-foreground)]">
                  Select a gift card to preview its printable QR card.
                </div>
              )}
            </div>
          </section>
        </div>
      ) : null}

      {activeTab === "promo-codes" ? (
        <div className="grid gap-6 xl:grid-cols-[0.9fr,1.1fr]">
          <form onSubmit={handlePromoCodeSubmit} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Create promo code</h2>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Promo codes validate discount previews only. Usage is global; per-customer limits stay disabled until an apply endpoint records redemption.</p>
            <div className="mt-4 grid gap-3">
              <input value={promoCodeForm.code} onChange={(event) => setPromoCodeForm((current) => ({ ...current, code: event.target.value.toUpperCase() }))} placeholder="SPRING25" className="rounded-xl border border-[var(--color-border)] px-3 py-2 uppercase" />
              <select value={promoCodeForm.promoType} onChange={(event) => setPromoCodeForm((current) => ({ ...current, promoType: event.target.value as PromoCodeForm["promoType"] }))} className="rounded-xl border border-[var(--color-border)] px-3 py-2">
                <option value="percent_off">Percent off</option>
                <option value="fixed_off">Fixed amount off</option>
                <option value="free_service">Free service</option>
              </select>
              {promoCodeForm.promoType !== "free_service" ? (
                <input value={promoCodeForm.value} onChange={(event) => setPromoCodeForm((current) => ({ ...current, value: event.target.value }))} placeholder={promoCodeForm.promoType === "percent_off" ? "Discount percent" : "Discount cents"} className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
              ) : null}
              <div className="grid gap-3 md:grid-cols-2">
                <input value={promoCodeForm.usageLimit} onChange={(event) => setPromoCodeForm((current) => ({ ...current, usageLimit: event.target.value }))} placeholder="Usage limit" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                <input value={promoCodeForm.minimumSpendCents} onChange={(event) => setPromoCodeForm((current) => ({ ...current, minimumSpendCents: event.target.value }))} placeholder="Minimum spend (cents)" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
              </div>
              <input type="datetime-local" value={promoCodeForm.validTo} onChange={(event) => setPromoCodeForm((current) => ({ ...current, validTo: event.target.value }))} className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
              <label className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
                <input type="checkbox" checked={promoCodeForm.isActive} onChange={(event) => setPromoCodeForm((current) => ({ ...current, isActive: event.target.checked }))} />
                Active immediately
              </label>
            </div>
            <button type="submit" disabled={saving} className="mt-4 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
              Create promo code
            </button>
          </form>

          <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Active promo library</h2>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Validation does not increment usage counts; apply/redemption wiring is a separate checkout step.</p>
            <div className="mt-4 space-y-3">
              {promoCodes.map((promoCode) => (
                <div key={promoCode.id} className="rounded-xl border border-[var(--color-border)] p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-[var(--color-foreground)]">{promoCode.code}</h3>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${promoCode.isActive ? "bg-emerald-500/10 text-emerald-700" : "bg-slate-500/10 text-slate-600"}`}>
                          {promoCode.isActive ? "active" : "paused"}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-[var(--color-muted-foreground)]">
                        <span>{promoCode.promoType}</span>
                        <span>{promoCode.value === null ? "Free service" : promoCode.value}</span>
                        <span>{promoCode.usageCount}/{promoCode.usageLimit ?? "unlimited"} used</span>
                        <span>{promoCode.minimumSpendCents === null ? "No minimum" : `Min ${formatCurrency(promoCode.minimumSpendCents, "EUR")}`}</span>
                        <span>{promoCode.validTo ? `Expires ${formatExpiry(promoCode.validTo)}` : "No expiry"}</span>
                      </div>
                    </div>
                    <button type="button" onClick={() => void togglePromoCode(promoCode)} className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]">
                      {promoCode.isActive ? "Pause" : "Resume"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      ) : null}

      {activeTab === "ledger" ? (
        <div className="grid gap-6 xl:grid-cols-[0.95fr,1.05fr]">
          <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Accounts</h2>
                <p className="text-sm text-[var(--color-muted-foreground)]">Review balance, purchases, usage, and expiry.</p>
              </div>
              <div className="text-sm text-[var(--color-muted-foreground)]">{accounts.length} accounts</div>
            </div>
            <div className="mt-4 space-y-3">
              {accounts.map((account) => (
                <button
                  key={account.id}
                  type="button"
                  onClick={() => setSelectedAccount(account)}
                  className={`w-full rounded-xl border p-4 text-left ${selectedAccount?.id === account.id ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5" : "border-[var(--color-border)] hover:border-[var(--color-primary)]/40"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-[var(--color-foreground)]">{account.personName}</p>
                      <p className="text-sm text-[var(--color-muted-foreground)]">{account.personEmail || "No email"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-semibold text-[var(--color-foreground)]">{account.balance}</p>
                      <p className="text-xs text-[var(--color-muted-foreground)]">{account.accountType}</p>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-[var(--color-muted-foreground)]">
                    <span>Purchased {account.totalPurchased}</span>
                    <span>Used {account.totalUsed}</span>
                    <span>{account.packageName ? `Package ${account.packageName}` : "Manual ledger"}</span>
                    <span>{formatExpiry(account.expiresAt)}</span>
                  </div>
                </button>
              ))}
            </div>
          </section>

          <section className="space-y-6">
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Manual credit adjustment</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Apply a manager-side ledger credit to the selected account.
              </p>
              {selectedAccount ? (
                <form onSubmit={addManualCredits} className="mt-4 flex flex-col gap-3 md:flex-row">
                  <input value={manualAddAmount} onChange={(event) => setManualAddAmount(event.target.value)} placeholder="Amount" className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                  <button type="submit" disabled={saving} className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
                    Add credits to {selectedAccount.personName}
                  </button>
                </form>
              ) : (
                <p className="mt-4 text-sm text-[var(--color-muted-foreground)]">Select an account to post a manual credit.</p>
              )}
            </div>

            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Ledger</h2>
              {selectedAccount ? (
                <div className="mt-4 space-y-3">
                  {transactions.map((transaction) => (
                    <div key={transaction.id} className="rounded-xl border border-[var(--color-border)] p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <p className="font-medium text-[var(--color-foreground)]">{transaction.description}</p>
                          <div className="mt-1 flex flex-wrap gap-3 text-xs text-[var(--color-muted-foreground)]">
                            <span>{transaction.type}</span>
                            <span>{formatDateTime(transaction.createdAt)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-sm font-semibold ${transaction.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                            {transaction.amount > 0 ? "+" : ""}{transaction.amount}
                          </span>
                          {transaction.type === "usage" ? (
                            <button
                              type="button"
                              onClick={() => setRefundDraft({ transactionId: transaction.id, maxAmount: Math.abs(transaction.amount), amount: String(Math.abs(transaction.amount)) })}
                              className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                            >
                              Refund
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-4 text-sm text-[var(--color-muted-foreground)]">Select an account to view the ledger.</p>
              )}
            </div>

            {refundDraft ? (
              <form onSubmit={submitRefund} className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5">
                <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Process refund</h2>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                  Refund up to {refundDraft.maxAmount} credits from the selected debit transaction.
                </p>
                <div className="mt-4 flex flex-col gap-3 md:flex-row">
                  <input value={refundDraft.amount} onChange={(event) => setRefundDraft((current) => current ? { ...current, amount: event.target.value } : current)} className="rounded-xl border border-[var(--color-border)] px-3 py-2" />
                  <button type="submit" disabled={saving} className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
                    Confirm refund
                  </button>
                  <button type="button" onClick={() => setRefundDraft(null)} className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)]">
                    Cancel
                  </button>
                </div>
              </form>
            ) : null}
          </section>
        </div>
      ) : null}
    </div>
  );
}
